import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Box } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { AssetSelect, EditPage, FuseInput } from '/src/components';
import { usePaymentStore } from '/src/context';
import { toastMessages } from '/src/lib/messages';
import { Note } from '/src/lib/models';
import { DataModelType, FormErrors, UpdatePaymentForm } from '/src/lib/types';
import { isNumber } from '/src/utils';

/**
 * Edit Payment Page
 */
export const EditPaymentPage = observer(() => {
  /** Context **/
  const paymentStore = usePaymentStore();
  const params = useParams();
  const navigate = useNavigate();

  /** State **/
  const [formValues, setFormValues] = useState<UpdatePaymentForm>();
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  /**
   * Array of booleans indicating which sidebar items are open.
   *
   * @remarks
   * Each item's index in the array corresponds to its position in the sidebar.
   */
  const [sidebarItems, setSidebarItems] = useState<boolean[]>([
    false, // Notes
    false, // Files
  ]);

  /** Computed **/
  const payment = paymentStore.currentPayment;

  /** Methods **/

  /**
   * Updates a form value. Designed so that, in a form field's `onChange` method,
   * you pass this function with the name of the prop you want to update, and it
   * returns a function that will automatically handle saving the returned value.
   *
   * @param key Name of the form prop to update.
   */
  const updateFormValue = (key: string) => (value: any) => {
    if (formValues) {
      setFormValues({
        ...formValues,
        [key]: value,
      });
      // Clear error when field is updated
      if (formErrors[key]) {
        setFormErrors({
          ...formErrors,
          [key]: '',
        });
      }
    }
  };

  /**
   * Validates the form data. If errors are detected, it saves them to `formErrors` and returns false.
   *
   * @returns Boolean indicating whether the data is valid
   */
  const validateForm = () => {
    const errors: FormErrors = {};

    if (!formValues?.name) errors.name = 'Please enter a name';
    if (!isNumber(formValues?.amount) || !formValues?.amount) errors.amount = 'Please enter an amount';
    if (!formValues?.paymentType) errors.paymentType = 'Please select a payment type';
    if (!formValues?.datePaid) errors.datePaid = 'Please select a date paid';
    if (!formValues?.dateDeposited) errors.dateDeposited = 'Please select a date deposited';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Updates the Payment.
   *
   * @remarks
   * Redirects to the Payment's Detail page afterwards.
   */
  const update = async () => {
    if (!formValues) return;

    if (!validateForm()) {
      toast.error('Please fix the errors before submitting');
      return;
    }

    try {
      const response = await paymentStore.update(formValues);
      if (!response) throw new Error(`Could not update Payment with ID "${formValues._id}"`);

      toast.success('Payment Updated');
      navigate(`/payments/${response._id}`);
    } catch (err) {
      console.error(err);
      toast.error(toastMessages.updatePayment.error);
    }
  };

  /**
   * Updates the Notes list.
   */
  const updateNotes = async (notes: Note[]) => {
    if (!formValues) return;

    await paymentStore.update({ ...formValues, notes });

    updateFormValue('notes')(notes);
  };

  /**
   * Updates the Documents list.
   *
   * @param files List of Document URLs.
   */
  const updateFiles = async (files: string[]) => {
    if (!formValues) return;

    await paymentStore.update({ ...formValues, files });

    updateFormValue('files')(files);
  };

  /** Effects **/

  useEffect(() => {
    params.paymentId && paymentStore.get(params.paymentId);
  }, [params.paymentId]);

  useEffect(() => {
    if (paymentStore.currentPayment) setFormValues(paymentStore.currentPayment);
  }, [paymentStore.currentPayment]);

  /** Render **/
  return (
    <EditPage
      modelName={paymentStore.currentPayment?.name ?? ''}
      modelType={DataModelType.Payment}
      onEdit={update}
      sidebarFlex="35"
      contentFlex="65"
      sidebarItems={sidebarItems}
      setSidebarItems={setSidebarItems}
      noteOptions={{
        notes: formValues?.notes ?? [],
        onChange: updateNotes,
      }}
      documentOptions={{
        files: formValues?.files ?? [],
        onChange: updateFiles,
      }}
    >
      {/* Asset */}
      <AssetSelect
        value={payment?.asset._id ?? ''}
        setValue={() => {}}
        assets={payment?.asset ? [payment.asset] : []}
        disabled
      />

      {/* Name */}
      <FuseInput
        label="Payment Name"
        field="name"
        formValues={formValues}
        updateValue={updateFormValue('name')}
        required
        error={formErrors.name}
      />

      {/* Amount */}
      <Box display="flex" justifyContent="center" alignItems="center" gap="16px">
        <FuseInput
          label="Amount"
          field="amount"
          formValues={formValues}
          updateValue={updateFormValue('amount')}
          min="0"
          moneyMask
          required
          error={formErrors.amount}
        />

        {/* Payment Type */}
        <FuseInput
          label="Payment Type"
          field="paymentType"
          formValues={formValues}
          updateValue={updateFormValue('paymentType')}
          required
          error={formErrors.paymentType}
        />
      </Box>

      <Box display="flex" justifyContent="center" alignItems="center" gap="16px">
        {/* Date Paid */}
        <FuseInput
          label="Date Paid"
          value={formValues?.datePaid}
          updateValue={updateFormValue('datePaid')}
          type="date"
          required
          error={formErrors.datePaid}
          boxProps={{ className: 'pad-bottom-xs' }}
        />

        {/* Date Deposited */}
        <FuseInput
          label="Date Deposited"
          value={formValues?.dateDeposited}
          updateValue={updateFormValue('dateDeposited')}
          type="date"
          required
          error={formErrors.dateDeposited}
          boxProps={{ className: 'pad-bottom-xs' }}
        />
      </Box>
    </EditPage>
  );
});
